import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'antd/lib/button';
import message from 'antd/lib/message';
import { archiveArticle, listArticles } from '../api/articles';
import { mapApiArticleToCard } from '../api/articles.mappers';
import ArticlesSection from '../components/knowledge-base/ArticlesSection';
import FooterBar from '../components/helpdesk/FooterBar';
import HeaderBar from '../components/helpdesk/HeaderBar';
import ManagementPageSkeleton from '../components/management/ManagementPageSkeleton';
import StatsCards from '../components/knowledge-base/StatsCards';
import { knowledgeStatusOptions } from '../data/knowledgeBase';
import '../styles/helpdesk.scss';

function buildFilterOptions(values, allLabel) {
  const normalizeOptionValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value);

    if (typeof value === 'object') {
      if (value.name) return String(value.name);
      if (value.title) return String(value.title);
      if (value.slug) return String(value.slug);
      if (value.id) return String(value.id);
    }

    return '';
  };

  const normalizedValues = values.map(normalizeOptionValue).filter(Boolean);

  return [
    { value: 'all', label: allLabel },
    ...Array.from(new Set(normalizedValues)).map((value) => ({ value, label: value })),
  ];
}

function KnowledgeBasePage() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingArticleId, setDeletingArticleId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [categoryOptions, setCategoryOptions] = useState([{ value: 'all', label: 'All Categories' }]);
  const [tagOptions, setTagOptions] = useState([{ value: 'all', label: 'All Tags' }]);
  const [totalCount, setTotalCount] = useState(0);

  const loadFilterOptions = useCallback(async () => {
    try {
      const response = await listArticles({ page: 1, pageSize: 200 });
      const mappedArticles = response.items.map(mapApiArticleToCard);
      setCategoryOptions(
        buildFilterOptions(
          mappedArticles.map((item) => item.category),
          'All Categories',
        ),
      );
      setTagOptions(
        buildFilterOptions(
          mappedArticles.flatMap((item) => item.tags || []),
          'All Tags',
        ),
      );
    } catch {
      setCategoryOptions([{ value: 'all', label: 'All Categories' }]);
      setTagOptions([{ value: 'all', label: 'All Tags' }]);
    }
  }, []);

  const loadArticles = useCallback(async (filters) => {
    setLoading(true);
    const applyDefaultArchiveRule = (list) => {
      if (filters.status !== 'all') return list;
      return list.filter((item) => item.status !== 'Archived');
    };

    try {
      const response = await listArticles({
        page: 1,
        pageSize: 100,
        status: filters.status,
        category: filters.category,
        tag: filters.tag,
        ...filters,
      });
      const mappedArticles = response.items.map(mapApiArticleToCard);
      const normalizedList = applyDefaultArchiveRule(mappedArticles);
      setArticles(normalizedList);
      setTotalCount(normalizedList.length);
    } catch {
      try {
        const fallbackResponse = await listArticles({ page: 1, pageSize: 200 });
        const mappedArticles = fallbackResponse.items.map(mapApiArticleToCard);
        const filtered = mappedArticles.filter((article) => {
          const statusOk = filters.status === 'all' || article.status.toUpperCase() === filters.status;
          const categoryOk = filters.category === 'all' || article.category === filters.category;
          const tagOk = filters.tag === 'all' || (article.tags || []).includes(filters.tag);
          return statusOk && categoryOk && tagOk;
        });
        const normalizedList = applyDefaultArchiveRule(filtered);
        setArticles(normalizedList);
        setTotalCount(normalizedList.length);
      } catch {
        message.error('Unable to load articles. Please try again.');
        setArticles([]);
        setTotalCount(0);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    loadArticles({
      status: statusFilter,
      category: categoryFilter,
      tag: tagFilter,
    });
  }, [loadArticles, statusFilter, categoryFilter, tagFilter]);

  const visibleArticles = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return articles;

    return articles.filter((article) => {
      const searchable = [
        article.title,
        article.description,
        article.author,
        article.category,
        ...(article.tags || []),
      ]
        .join(' ')
        .toLowerCase();

      return searchable.includes(normalizedSearch);
    });
  }, [articles, searchTerm]);

  const articleStats = useMemo(() => {
    const publishedCount = visibleArticles.filter((article) => article.status === 'Published').length;
    const draftCount = visibleArticles.filter((article) => article.status === 'Draft').length;

    return [
      { id: 'total', label: 'Total Articles', value: visibleArticles.length, tone: 'default' },
      { id: 'published', label: 'Published', value: publishedCount, tone: 'success' },
      { id: 'drafts', label: 'Drafts', value: draftCount, tone: 'warning' },
    ];
  }, [visibleArticles]);

  const statusSummary =
    statusFilter === 'all' ? 'All statuses' : `${statusFilter.charAt(0)}${statusFilter.slice(1).toLowerCase()}`;

  const handleDeleteArticle = async (article) => {
    const identifier = article.deleteIdentifier || article.backendId || article.slug || article.id;
    if (!identifier) return;

    setDeletingArticleId(article.id || identifier);
    try {
      await archiveArticle(identifier);
      message.success('Article deleted successfully.');
      setArticles((previous) => previous.filter((item) => item.id !== article.id));
      await loadArticles({
        status: statusFilter,
        category: categoryFilter,
        tag: tagFilter,
      });
      await loadFilterOptions();
    } catch {
      message.error('Unable to delete article. Please try again.');
    } finally {
      setDeletingArticleId('');
    }
  };

  const showManagementSkeleton = loading && articles.length === 0;

  return (
    <div className="helpdesk-page">
      <HeaderBar />
      <main className="helpdesk-main">
        <div className="helpdesk-main__inner">
          <section className="kb-page" aria-labelledby="kb-title">
            {showManagementSkeleton ? (
              <ManagementPageSkeleton statsCount={3} toolbarCount={4} rowCount={6} />
            ) : (
              <>
                <div className="kb-page__header">
                  <div>
                    <h1 id="kb-title">Articles</h1>
                    <p>Manage your content and publications</p>
                  </div>
                  <Button
                    type="primary"
                    className="kb-page__action"
                    onClick={() => navigate('/knowledge-base/create-article')}
                  >
                    Create Article
                  </Button>
                </div>

                <section className="kb-page__overview" aria-label="Dashboard overview">
                  <div className="kb-page__overview-copy">
                    <h2>Content Dashboard</h2>
                    <p>
                      Track publishing workflow, find drafts quickly, and keep your knowledge base up to date.
                    </p>
                  </div>
                  <div className="kb-page__overview-meta">
                    <span>{statusSummary}</span>
                    <span>{visibleArticles.length} article(s)</span>
                  </div>
                </section>

                <div className="kb-page__workspace">
                  <StatsCards stats={articleStats} />
                  <ArticlesSection
                    articles={visibleArticles}
                    statusOptions={knowledgeStatusOptions}
                    categoryOptions={categoryOptions}
                    tagOptions={tagOptions}
                    searchValue={searchTerm}
                    statusValue={statusFilter}
                    categoryValue={categoryFilter}
                    tagValue={tagFilter}
                    loading={loading}
                    onSearchChange={setSearchTerm}
                    onStatusChange={setStatusFilter}
                    onCategoryChange={setCategoryFilter}
                    onTagChange={setTagFilter}
                    onDeleteArticle={handleDeleteArticle}
                    deletingArticleId={deletingArticleId}
                    totalCount={totalCount || visibleArticles.length}
                  />
                </div>
              </>
            )}
          </section>
        </div>
      </main>
      <FooterBar />
    </div>
  );
}

export default KnowledgeBasePage;
